"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.generatedCalculationInputsPage = generatedCalculationInputsPage;
exports.generatedWorkbook = generatedWorkbook;
const uom_1 = require("@genesys/uom");
const VelocityPage = __importStar(require("./velocity-page"));
/**
 * Builds the "Calculation inputs" sheet from server-generated operating points, in the layout the
 * express importer expects.
 *
 * The units and header rows are taken from the blank template rather than restated here, and the
 * per-column units are then read back OUT of that units row. So the generated sheet cannot drift
 * from what the importer parses: there is exactly one definition of the layout, and it is the one
 * already in use.
 *
 * `si` picks EU or US units, exactly as it does for the blank template - the values come from the
 * server as unit-carrying amounts and are converted here.
 */
function generatedCalculationInputsPage(coils, si) {
    // Always the fluid-cooling layout: a generated sheet carries both the dry and the wet block,
    // which is the 20-column form.
    const template = VelocityPage.velocityPage("fluid-cooling", si);
    // Two warning columns appended to the right of the 20 the importer knows about. They are safe to
    // carry: parse-fluid-cooling picks columns 0-19 by index, so anything beyond is never read, and
    // the sheet can be uploaded back for fitting with the warnings still on it.
    const unitsRow = [...template[0], "", ""];
    const headersRow = [...template[1], "Dry warning", "Wet warning"];
    const units = unitsRow.map(u => uom_1.Serialize.stringToUnit(u.substring(1, u.length - 1)));
    const cell = (serialized, column) => {
        const unit = units[column];
        if (!unit) {
            throw new Error(`Generated inputs: no unit for column ${column} ("${headersRow[column]}", ` +
                `unit cell "${unitsRow[column]}").`);
        }
        const amount = uom_1.Serialize.stringToAmount(serialized);
        if (!amount) {
            // Loud rather than blank: this is where a mismatch between the server's amount
            // serialisation and the client's parser would show up, and a silently empty sheet would be
            // far harder to diagnose than a thrown message naming the value.
            throw new Error(`Generated inputs: could not read "${serialized}" for column ${column} ` +
                `("${headersRow[column]}").`);
        }
        const format = uom_1.Format.getUnitFormat(unit, uom_1.UnitsFormat);
        return uom_1.Amount.valueAs(unit, amount).toFixed(format ? format.decimalCount : 2);
    };
    // Sheet column order. The four powers and two pressure drops arrive as zero - they are what the
    // supplier fills in - and the weights arrive prefilled at 1.
    const cells = (r) => [
        r.airFlow,
        r.airInPressure,
        r.dryAirInTemperature,
        r.dryAirInHumidity,
        r.dryFluidTemperatureIn,
        r.dryFluidFlow,
        r.dryWeightTotalPower,
        r.dryTotalPower,
        r.dryWeightPressureDrop,
        r.dryPressureDrop,
        r.wetAirInTemperature,
        r.wetAirInHumidity,
        r.wetFluidTemperatureIn,
        r.wetFluidFlow,
        r.wetWeightTotalPower,
        r.wetTotalPower,
        r.wetWeightSensiblePower,
        r.wetSensiblePower,
        r.wetWeightPressureDrop,
        r.wetPressureDrop
    ]
        .map(cell)
        // Straight through, not via `cell` - these are prose, not amounts.
        .concat([r.dryWarning, r.wetWarning]);
    const blank = unitsRow.map(() => "");
    const data = [unitsRow, headersRow];
    coils.forEach((coil, ix) => {
        // One block per coil, split on a blank air-flow cell - the same separator the importer uses to
        // pair blocks back up with geometry rows.
        if (ix > 0) {
            data.push(blank);
        }
        coil.rows.forEach(r => data.push(cells(r)));
    });
    return data;
}
/**
 * The uploaded Geometry sheet with a Warnings column appended, carrying the warnings that belong to
 * the COIL rather than to any one operating point - an air-flow span too narrow to identify the
 * Reynolds exponent, or a geometry that cannot be characterised at all under the velocity limits.
 * Those have no row on the Calculation inputs sheet to sit on; in the worst case no rows are
 * generated for that coil whatsoever, and this is the only place the reason can appear.
 *
 * Appending is safe on re-import: parse-geometry2 looks columns up by header name, so a column it
 * does not know about is ignored rather than misread.
 */
function geometryWithWarnings(geometrySheet, coils) {
    const names = geometrySheet[1].map(n => n.trim().toLowerCase());
    const idColumn = names.indexOf("id");
    if (idColumn < 0) {
        throw new Error("Generated inputs: the Geometry sheet has no 'id' column, so warnings cannot be matched to coils.");
    }
    return geometrySheet.map((row, ix) => {
        if (ix === 0) {
            return [...row, ""];
        }
        if (ix === 1) {
            return [...row, "Warnings"];
        }
        const coil = coils.find(c => c.coilId === row[idColumn]);
        return [...row, coil ? coil.warnings.join(" ") : ""];
    });
}
/**
 * The workbook to hand back: the uploaded Geometry sheet plus the generated Calculation inputs.
 * Sheet names match the blank template so the result can be uploaded straight back for fitting once
 * the supplier has filled it in.
 */
function generatedWorkbook(geometrySheet, coils, si) {
    return [
        { name: "Geometry", data: geometryWithWarnings(geometrySheet, coils) },
        { name: "Calculation inputs", data: generatedCalculationInputsPage(coils, si) }
    ];
}
//# sourceMappingURL=generated-calculation-inputs.js.map